import { Component, Input, OnInit } from '@angular/core';
import { AlertController, ModalController } from '@ionic/angular';
import { MenuItem } from 'primeng/api';
import * as XLSX from 'xlsx';
import { GlobalService } from 'src/app/api/global.service';
import { ApiService } from 'src/app/api/api.service';
import { SelectComponent as TagsSelectPage } from 'src/app/components/tags/select/select.component';
import { ManagerComponent as GroupsManagerPage } from 'src/app/components/groups/manager/manager.component';
import { parseNamesFromDisplayName } from 'src/app/services/name-parser.util';

export interface FilePreviewData {
  rowCount: number;
  columnCount: number;
  sheetName: string;
  headers: string[];
  sampleData: any[];
}

export interface FieldMapping {
  excelColumn: string;
  contactField: string;
  sampleValues: string[];
  columnIndex: number;
}

export interface ContactField {
  key: string;
  label: string;
  required: boolean;
}

export interface SimilarNameGroup {
  names: string[];
  contactsByName: { [name: string]: any[] };
  selectedName: string;
  customName: string;
  useCustomName: boolean;
  dismissed: boolean;
}

@Component({
  selector: 'app-import',
  templateUrl: './import.page.html',
  styleUrls: ['./import.page.scss'],
})
export class ImportPage implements OnInit {

  // When set (e.g. by the Client Track import wizard), skips the manual upload step entirely -
  // ProcessFile() runs against this instead of a user-picked file, landing straight on "Remap
  // Fields" once parsing succeeds, same as if the file had been dropped in by hand.
  @Input() preloadedFile: File | null = null;

  // Column names (matching rawHeaders) to leave out of the primary field-mapping list - they still
  // stay in rawHeaders so the Custom Field builder below can pick them, since we don't know what a
  // user might still want as a custom field.
  @Input() excludeFromMapping: string[] = [];

  // Pre-resolved groups to attach to each contact row, index-aligned with parsedData (e.g. by the
  // Client Track import wizard, matching a T1-eligible contact to the "T1 Client" group). Merged in
  // during ProcessMappings() alongside whatever the column mapping produces.
  @Input() presetGroupsByRow: any[][] = [];

  // Available contact fields for mapping
  contactFields: ContactField[] = [
    { key: '', label: '-- Select a field --', required: false },
    { key: 'display_name', label: 'Display Name', required: true },
    { key: 'first_name', label: 'First Name', required: true },
    { key: 'last_name', label: 'Last Name', required: true },
    { key: 'middle_name', label: 'Middle Name', required: false },
    { key: 'email', label: 'Email', required: false },
    { key: 'phone_number', label: 'Phone Number', required: false },
    { key: 'cell_phone', label: 'Cell Phone', required: false },
    { key: 'alt_phone', label: 'Alt Phone', required: false },
    { key: 'fax', label: 'Fax', required: false },
    { key: 'str_address', label: 'Street Address', required: false },
    { key: 'city', label: 'City', required: false },
    { key: 'province', label: 'Province/State', required: false },
    { key: 'country', label: 'Country', required: false },
    { key: 'postal_code', label: 'Postal/Zip Code', required: false },
    { key: 'website', label: 'Website', required: false },
    { key: 'custom_id', label: 'Custom ID', required: false },
    { key: 'gender', label: 'Gender', required: false },
    { key: 'birthday', label: 'Birthday', required: false },
    { key: 'anniversary', label: 'Anniversary', required: false },
    { key: 'sin', label: 'SIN/SSN', required: false },
    { key: 'spoken_language', label: 'Spoken Language', required: false },
    { key: 'custom_data', label: 'Notes/Custom Data', required: false },
    // Not a real client field - carried through per-row so LinkSpouseRelationships() can look up
    // and link a "Spouse" family relationship by display name after every contact is created.
    { key: 'spouse_display_name', label: 'Spouse Display Name', required: false },
    // The rest of the spouse's fields. Like spouse_display_name these aren't client fields on this contact: when any of them
    // has a value the row also creates the spouse as their own contact (see BuildSpouseFromColumns) and links the two
    // as spouses.
    { key: 'spouse_first_name', label: 'Spouse First Name', required: false },
    { key: 'spouse_middle_name', label: 'Spouse Middle Name', required: false },
    { key: 'spouse_last_name', label: 'Spouse Last Name', required: false },
    { key: 'spouse_email', label: 'Spouse Email', required: false },
    { key: 'spouse_phone_number', label: 'Spouse Phone Number', required: false },
    { key: 'spouse_cell_phone', label: 'Spouse Cell Phone', required: false },
    { key: 'spouse_alt_phone', label: 'Spouse Alt Phone', required: false },
    { key: 'spouse_fax', label: 'Spouse Fax', required: false },
    { key: 'spouse_str_address', label: 'Spouse Street Address', required: false },
    { key: 'spouse_city', label: 'Spouse City', required: false },
    { key: 'spouse_province', label: 'Spouse Province/State', required: false },
    { key: 'spouse_country', label: 'Spouse Country', required: false },
    { key: 'spouse_postal_code', label: 'Spouse Postal/Zip Code', required: false },
    { key: 'spouse_website', label: 'Spouse Website', required: false },
    { key: 'spouse_custom_id', label: 'Spouse Custom ID', required: false },
    { key: 'spouse_gender', label: 'Spouse Gender', required: false },
    { key: 'spouse_birthday', label: 'Spouse Birthday', required: false },
    { key: 'spouse_anniversary', label: 'Spouse Anniversary', required: false },
    { key: 'spouse_sin', label: 'Spouse SIN/SSN', required: false },
    { key: 'spouse_spoken_language', label: 'Spouse Spoken Language', required: false },
    { key: 'spouse_custom_data', label: 'Spouse Notes/Custom Data', required: false },
  ];

  activeIndex = 0;
  items: MenuItem[];

  // File upload state
  uploadedFile: File | null = null;
  isDragOver = false;
  isUploading = false;
  uploadProgress = 0;
  uploadError: string | null = null;
  filePreviewData: FilePreviewData | null = null;

  // Parsed data
  parsedData: any[] = [];
  rawHeaders: string[] = [];

  // Field mapping state
  fieldMappings: FieldMapping[] = [];

  // Preview/Review state
  mappedContacts: any[] = [];
  previewContacts: any[] = [];
  validationErrors: string[] = [];

  // Validation tracking
  hasValidationErrors = false;
  validContactsCount = 0;
  invalidContactsCount = 0;
  isValidated = false; // Has validation been run?
  isValidating = false; // Is validation currently running?

  // Pagination
  currentPage = 1;
  pageSize = 25;

  // Duplicate name tracking
  duplicateNames: { name: string; indices: number[] }[] = [];
  hasDuplicateNames = false;

  // Existing email conflict tracking
  existingEmails: string[] = [];
  existingEmailConflicts: { email: string; rowNumber: number; displayName: string }[] = [];
  hasExistingEmailConflicts = false;

  // Existing name match warnings (not blocking)
  existingClientNames: { fullName: string; displayName: string }[] = [];
  existingNameEmailPairs: { displayName: string; email: string }[] = [];

  // Exact system duplicates: contacts matching both display_name + email of an existing record
  exactSystemDuplicates: { rowNumber: number; displayName: string; email: string }[] = [];
  hasExactSystemDuplicates = false;
  existingNameMatches: { rowNumber: number; displayName: string; matchType: string; matchedName: string }[] = [];
  hasExistingNameMatches = false;

  // Internal email duplicate groups (for merge option)
  emailDuplicateGroups: { email: string; contacts: any[]; mergeEnabled: boolean }[] = [];
  hasInternalEmailDuplicates = false;

  // View mode: 'compact' for table view, 'comfort' for card view
  viewMode: 'compact' | 'comfort' = 'compact';

  // When true (and only meaningful once isValidated), the table/cards show only contacts with
  // validation errors - lets you find the handful of bad rows in an otherwise huge import instead of
  // paging through everything.
  showErrorsOnly = false;

  // Display name builder
  displayNameParts: string[] = [];
  displayNameSeparator: string = ' ';
  displayNameColumnToAdd: string = '';

  // Data quality issues
  dataQualityIssues: string[] = [];
  hasDataQualityIssues = false;
  // Similar company name groups (e.g. "Jim's Garage" vs "Jims Garage")
  similarNameGroups: SimilarNameGroup[] = [];
  hasSimilarNameGroups = false;
  // Group assignment
  availableGroups: any[] = [];
  selectedGroups: any[] = [];

  // Tag assignment
  availableTags: any[] = [];
  selectedTags: any[] = [];

  // Custom field definitions for import mapping
  customFieldDefinitions: { name: string; sourceColumn: string }[] = [];

  constructor(private modalController: ModalController, public globalService: GlobalService, private apiService: ApiService, private alertController: AlertController) { }

  ngOnInit() {
    this.items = [
      { label: 'Upload Excel Sheet' },
      { label: 'Remap Fields' },
      { label: 'Validate' }
    ];
    this.LoadGroups();
    this.LoadTags();
    if (this.preloadedFile) this.ProcessFile(this.preloadedFile);
  }

  async LoadGroups() {
    try {
      const data: any = await this.apiService.GetGroups('clients');
      this.availableGroups = data?.data || [];
    } catch (e) {
      this.availableGroups = [];
    }
  }

  async LoadTags() {
    try {
      const data: any = await this.apiService.GetTags('clients');
      this.availableTags = data?.data || [];
    } catch (e) {
      this.availableTags = [];
    }
  }

  HasClientsSelected(): boolean {
    return this.mappedContacts.some(c => c.is_a_client);
  }

  async presentSelectGroupsModal() {
    const modal = await this.modalController.create({
      component: GroupsManagerPage,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'clients',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...this.selectedGroups],
        modal_title: 'Select Groups'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        this.selectedGroups = data.data;
      }
    });
    return await modal.present();
  }

  async presentSelectTagsModal() {
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'clients',
        selected_tags: this.selectedTags
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        this.selectedTags = data.data;
      }
    });
    return await modal.present();
  }

  // Per-contact equivalents of presentSelectGroupsModal/presentSelectTagsModal above - used instead of
  // the bulk "assign to all" pickers for a CTD-sourced import (see preloadedFile), where a single
  // set of groups/tags rarely makes sense across a whole legacy export.
  async presentSelectGroupsModalForContact(contact: any) {
    const modal = await this.modalController.create({
      component: GroupsManagerPage,
      cssClass: 'medium-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'clients',
        selection_mode: true,
        max_selection: 999,
        selected_groups: [...(contact.groups || [])],
        modal_title: 'Select Groups'
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data && data.data) {
        contact.groups = data.data;
        this.OnDataChanged();
      }
    });
    return await modal.present();
  }

  async presentSelectTagsModalForContact(contact: any) {
    const modal = await this.modalController.create({
      component: TagsSelectPage,
      cssClass: 'small-modal tall-modal secondary-modal',
      backdropDismiss: true,
      componentProps: {
        url: 'clients',
        selected_tags: contact.tags || []
      }
    });
    modal.onDidDismiss().then((data: any) => {
      if (data.data) {
        contact.tags = data.data;
        this.OnDataChanged();
      }
    });
    return await modal.present();
  }

  DownloadExampleTemplate(): void {
    // Create headers from contact fields (excluding the placeholder)
    const headers = this.contactFields
      .filter(f => f.key !== '')
      .map(f => f.label);

    // Create example data row
    const exampleData = [
      'John Doe DELETE THIS ROW BEFORE USING',  // Display Name
      'John',                   // First Name
      'Doe',                    // Last Name
      'M',                      // Middle Name
      'sales@fusionworkflow.com', // Email
      '555-123-4567',           // Phone Number
      '555-987-6543',           // Cell Phone
      '555-111-2222',           // Alt Phone
      '555-123-4568',           // Fax
      '123 Main Street',        // Street Address
      'Toronto',                // City
      'ON',                     // Province/State
      'Canada',                 // Country
      'M5V 1A1',                // Postal/Zip Code
      'www.fusionworkflow.com', // Website
      'CUST-001',               // Custom ID
      'Male',                   // Gender
      '1990-01-15',             // Birthday
      '2015-06-20',             // Anniversary
      '',                       // SIN/SSN
      'English',                // Spoken Language
      'Example contact notes'   // Notes/Custom Data
    ];

    // Create worksheet with headers and example row
    const worksheetData = [headers, exampleData];
    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

    // Set column widths for better readability
    worksheet['!cols'] = headers.map(() => ({ wch: 25 }));

    // Create workbook and add worksheet
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Contacts');

    // Generate and download the file
    XLSX.writeFile(workbook, 'Fusionworkflow_Contact_Import_Template.xlsx');
  }


  async Close() {
    await this.modalController.dismiss();
  }

  async GoToStep(step: number) {
    this.activeIndex = step;
  }

  NextStep() {
    if (this.activeIndex === 1) {
      // Moving from mapping to review - process the mappings
      this.ProcessMappings();
    }
    if (this.activeIndex < this.items.length - 1) {
      this.activeIndex++;
    }
  }

  PreviousStep() {
    if (this.activeIndex > 0) {
      this.activeIndex--;
    }
  }

  // Drag and drop handlers
  OnDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = true;
  }

  OnDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;
  }

  OnDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragOver = false;

    const files = event.dataTransfer?.files;
    if (files && files.length > 0) {
      this.ProcessFile(files[0]);
    }
  }

  OnFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.ProcessFile(input.files[0]);
    }
  }

  ProcessFile(file: File): void {
    // Reset state
    this.uploadError = null;
    this.filePreviewData = null;
    this.parsedData = [];
    this.rawHeaders = [];
    this.fieldMappings = [];
    this.dataQualityIssues = [];
    this.hasDataQualityIssues = false;
    this.displayNameParts = [];
    this.displayNameSeparator = ' ';
    this.displayNameColumnToAdd = '';

    // Validate file type by extension
    const extension = file.name.split('.').pop()?.toLowerCase();
    const validExtensions = ['xlsx', 'xls', 'csv'];

    if (!validExtensions.includes(extension || '')) {
      this.uploadError = 'Invalid file type. Please upload an Excel (.xlsx, .xls) or CSV file.';
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      this.uploadError = 'File is too large. Maximum file size is 10MB.';
      return;
    }

    this.uploadedFile = file;
    this.isUploading = true;
    this.uploadProgress = 0;

    // Read and parse the file
    const reader = new FileReader();

    reader.onprogress = (event) => {
      if (event.lengthComputable) {
        this.uploadProgress = Math.round((event.loaded / event.total) * 50);
      }
    };

    reader.onload = (event) => {
      try {
        this.uploadProgress = 60;
        const data = event.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });

        this.uploadProgress = 80;

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Convert to JSON with header row
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        if (jsonData.length === 0) {
          this.uploadError = 'The file appears to be empty.';
          this.isUploading = false;
          this.uploadedFile = null;
          return;
        }

        // Extract headers (first row)
        // Array.from (not .map) - sheet_to_json returns sparse arrays, so blank header cells are
        // holes that .map would skip and leave undefined.
        this.rawHeaders = Array.from(jsonData[0] as any[], (h, index) =>
          h ? String(h).trim() : `Column ${index + 1}`
        );

        // Extract data rows (skip header)
        this.parsedData = jsonData.slice(1).filter((row: any[]) =>
          row && row.some(cell => cell !== null && cell !== undefined && cell !== '')
        );

        this.uploadProgress = 90;

        // Validate data quality
        this.ValidateDataQuality();

        // If there are critical issues, stop processing
        if (this.hasDataQualityIssues) {
          this.uploadProgress = 100;
          this.isUploading = false;
          // Don't clear uploadedFile so they can see what was uploaded
          return;
        }

        this.uploadProgress = 100;

        // Set preview data
        this.filePreviewData = {
          rowCount: this.parsedData.length,
          columnCount: this.rawHeaders.length,
          sheetName: firstSheetName,
          headers: this.rawHeaders,
          sampleData: this.parsedData.slice(0, 5)
        };

        // Initialize field mappings with auto-detection
        this.InitializeFieldMappings();

        this.isUploading = false;

        // A file provided programmatically (e.g. the Client Track import wizard) means the
        // "pick a file" step already happened for the user - skip straight to reviewing the mapping.
        if (this.preloadedFile) this.activeIndex = 1;
      } catch (error) {
        console.error('Error parsing file:', error);
        this.uploadError = 'Failed to parse the file. Please make sure it is a valid Excel or CSV file.';
        this.isUploading = false;
        this.uploadedFile = null;
      }
    };

    reader.onerror = () => {
      this.uploadError = 'Failed to read the file. Please try again.';
      this.isUploading = false;
      this.uploadedFile = null;
    };

    reader.readAsArrayBuffer(file);
  }

  ValidateDataQuality(): void {
    this.dataQualityIssues = [];

    // Check 1: No data rows
    if (this.parsedData.length === 0) {
      this.dataQualityIssues.push('No data rows found. The file only contains a header row or is empty.');
    }

    // Every check below is a heuristic for a messy hand-made spreadsheet - skipped for a preloaded
    // file (e.g. the Client Track import wizard), which is a known-good backend export. It can
    // legitimately have a wide legacy schema, very few rows (anyone already imported is dropped), and
    // rows far sparser than the rest (a spouse built from a contact's Spouse* columns).
    if (this.preloadedFile) {
      this.hasDataQualityIssues = this.dataQualityIssues.length > 0;
      return;
    }

    // Check 2: Too many columns (likely pivoted or complex data)
    if (this.rawHeaders.length > 50) {
      this.dataQualityIssues.push(`Too many columns (${this.rawHeaders.length}). Your data may need to be reformatted. A simple contact list should have fewer than 50 columns.`);
    }

    // Check 3: Headers look like data (numbers, dates, or very long text)
    const suspiciousHeaders = this.rawHeaders.filter(h => {
      const str = String(h);
      // Check if header is a number
      if (!isNaN(Number(str)) && str.trim() !== '') return true;
      // Check if header looks like a date
      if (/^\d{1,2}[\/\-]\d{1,2}[\/\-]\d{2,4}$/.test(str)) return true;
      // Check if header is very long (probably data, not a column name)
      if (str.length > 50) return true;
      return false;
    });

    if (suspiciousHeaders.length > this.rawHeaders.length / 2) {
      this.dataQualityIssues.push('The first row doesn\'t look like column headers. Make sure your file has a header row with column names like "First Name", "Last Name", "Email", etc.');
    }

    // Check 4: Too many unnamed columns
    const unnamedColumns = this.rawHeaders.filter(h => h.startsWith('Column ')).length;
    if (unnamedColumns > this.rawHeaders.length / 2) {
      this.dataQualityIssues.push(`Many columns are unnamed (${unnamedColumns} of ${this.rawHeaders.length}). Please add descriptive column headers to your file.`);
    }

    // // Check 5: Inconsistent row lengths (messy data)
    // const rowLengths = this.parsedData.map(row => (row as any[]).filter(cell => cell !== null && cell !== undefined && cell !== '').length);
    // if (rowLengths.length > 0) {
    //   const avgLength = rowLengths.reduce((a, b) => a + b, 0) / rowLengths.length;
    //   const inconsistentRows = rowLengths.filter(len => Math.abs(len - avgLength) > avgLength * 0.5).length;
    //   if (inconsistentRows > this.parsedData.length * 0.3) {
    //     this.dataQualityIssues.push('Many rows have inconsistent data. Some rows have significantly more or fewer filled cells than others. This may indicate merged cells or misaligned data.');
    //   }
    // }

    // // Check 6: Very sparse data (too many empty cells)
    // const totalCells = this.parsedData.length * this.rawHeaders.length;
    // let filledCells = 0;
    // this.parsedData.forEach(row => {
    //   (row as any[]).forEach(cell => {
    //     if (cell !== null && cell !== undefined && cell !== '') filledCells++;
    //   });
    // });
    // const fillRate = totalCells > 0 ? filledCells / totalCells : 0;
    // if (fillRate < 0.2 && this.parsedData.length > 5) {
    //   this.dataQualityIssues.push(`The data is very sparse (only ${Math.round(fillRate * 100)}% of cells have data). This may indicate the data isn't formatted correctly for import.`);
    // }

    // // Check 7: Extremely wide rows (might be a report, not a data list)
    // if (this.rawHeaders.length > 30 && this.parsedData.length < 10) {
    //   this.dataQualityIssues.push('The file has many columns but few rows. This looks like a report or summary, not a contact list. Please use a simple list format.');
    // }

    // Set the flag if there are issues
    this.hasDataQualityIssues = this.dataQualityIssues.length > 0;
  }

  InitializeFieldMappings(): void {
    // Track which fields have been auto-mapped to prevent duplicates
    const usedFields: string[] = [];

    this.fieldMappings = this.rawHeaders
      .map((header, index) => ({ header, index }))
      // Excluded columns still live in rawHeaders (for the Custom Field builder) but don't get a row
      // in the primary mapping list.
      .filter(({ header }) => !this.excludeFromMapping.includes(header))
      .map(({ header, index }) => {
        // Get sample values for this column
        const sampleValues = this.parsedData
          .slice(0, 3)
          .map(row => row[index])
          .filter(val => val !== null && val !== undefined && val !== '')
          .map(val => String(val));

        // Try to auto-map based on header name
        const autoMappedField = this.AutoMapField(header, usedFields);
        if (autoMappedField) {
          usedFields.push(autoMappedField);
        }

        return {
          excelColumn: header,
          contactField: autoMappedField,
          sampleValues: sampleValues,
          columnIndex: index
        };
      });
  }

  // Column names that must never be auto-mapped, even though they'd otherwise match a rule below
  // (e.g. "ClientIDStr" contains "clientid" and would falsely match the custom_id rule).
  private readonly noAutoMapHeaders: string[] = ['clientidstr'];

  // Auto-map Excel column to contact field based on name matching
  AutoMapField(header: string, usedFields: string[]): string {
    const normalizedHeader = header.toLowerCase().replace(/[^a-z0-9]/g, '');

    if (this.noAutoMapHeaders.includes(normalizedHeader)) {
      return '';
    }

    // Define mapping rules: normalized patterns -> field key
    const mappingRules: { patterns: string[]; field: string }[] = [
      // Checked before display_name's rule below - "Spouse Display Name" normalizes to
      // "spousedisplayname", which contains "displayname" and would otherwise match that rule
      // first. No bare 'spouse' pattern - that would substring-match "Spouse First Name",
      // "Spouse Last Name", "Spouse Email", etc. too, none of which are the display name.
      { patterns: ['spousefirstname', 'spousefirst', 'spousegivenname'], field: 'spouse_first_name' },
      { patterns: ['spousemiddlename', 'spousemiddle'], field: 'spouse_middle_name' },
      { patterns: ['spouselastname', 'spouselast', 'spousesurname'], field: 'spouse_last_name' },
      { patterns: ['spouseemail', 'spouseemailaddress'], field: 'spouse_email' },
      { patterns: ['spousecellphone', 'spousecell', 'spousemobile'], field: 'spouse_cell_phone' },
      { patterns: ['spousealtphone', 'spousealternatephone', 'spouseworkphone', 'spouseotherphone'], field: 'spouse_alt_phone' },
      { patterns: ['spousephonenumber', 'spousephone', 'spousehomephone'], field: 'spouse_phone_number' },
      { patterns: ['spousefax'], field: 'spouse_fax' },
      { patterns: ['spousestreetaddress', 'spouseaddress', 'spousestreet'], field: 'spouse_str_address' },
      { patterns: ['spousecity'], field: 'spouse_city' },
      { patterns: ['spouseprovince', 'spousestate'], field: 'spouse_province' },
      { patterns: ['spousecountry'], field: 'spouse_country' },
      { patterns: ['spousepostalcode', 'spousepostal', 'spousezipcode', 'spousezip'], field: 'spouse_postal_code' },
      { patterns: ['spousewebsite'], field: 'spouse_website' },
      { patterns: ['spousecustomid', 'spouseclientid'], field: 'spouse_custom_id' },
      { patterns: ['spousegender', 'spousesex'], field: 'spouse_gender' },
      { patterns: ['spousebirthday', 'spousebirthdate', 'spousedob', 'spousedateofbirth'], field: 'spouse_birthday' },
      { patterns: ['spouseanniversary'], field: 'spouse_anniversary' },
      { patterns: ['spousesin', 'spousessn', 'spousesocialsecurity'], field: 'spouse_sin' },
      { patterns: ['spousespokenlanguage', 'spouselanguage'], field: 'spouse_spoken_language' },
      { patterns: ['spousenotes', 'spousecomments', 'spousecustomdata', 'spouseremarks'], field: 'spouse_custom_data' },
      { patterns: ['spousedisplayname', 'spousefullname', 'spousename'], field: 'spouse_display_name' },
      { patterns: ['firstname', 'first', 'fname', 'givenname'], field: 'first_name' },
      { patterns: ['lastname', 'last', 'lname', 'surname', 'familyname'], field: 'last_name' },
      { patterns: ['middlename', 'middle', 'mname'], field: 'middle_name' },
      { patterns: ['displayname', 'display', 'fullname', 'companyname', 'name'], field: 'display_name' },
      { patterns: ['email', 'emailaddress', 'mail'], field: 'email' },
      { patterns: ['phone', 'phonenumber', 'telephone', 'tel', 'homephone'], field: 'phone_number' },
      { patterns: ['cell', 'cellphone', 'mobile', 'mobilephone'], field: 'cell_phone' },
      { patterns: ['altphone', 'alternatephone', 'otherphone', 'workphone', 'officephone'], field: 'alt_phone' },
      { patterns: ['fax', 'faxnumber'], field: 'fax' },
      { patterns: ['address', 'streetaddress', 'street', 'address1'], field: 'str_address' },
      { patterns: ['city', 'town'], field: 'city' },
      { patterns: ['province', 'state', 'region'], field: 'province' },
      { patterns: ['country', 'nation'], field: 'country' },
      { patterns: ['postal', 'postalcode', 'zip', 'zipcode', 'postcode'], field: 'postal_code' },
      { patterns: ['website', 'web', 'url', 'homepage'], field: 'website' },
      { patterns: ['customid', 'clientid', 'id', 'customerid'], field: 'custom_id' },
      { patterns: ['gender', 'sex'], field: 'gender' },
      { patterns: ['birthday', 'birthdate', 'dob', 'dateofbirth'], field: 'birthday' },
      { patterns: ['anniversary'], field: 'anniversary' },
      { patterns: ['sin', 'ssn', 'socialsecurity'], field: 'sin' },
      { patterns: ['language', 'spokenlanguage'], field: 'spoken_language' },
      { patterns: ['notes', 'comments', 'customdata', 'remarks'], field: 'custom_data' },
    ];

    for (const rule of mappingRules) {
      // Skip if this field is already used
      if (usedFields.includes(rule.field)) {
        continue;
      }

      for (const pattern of rule.patterns) {
        if (normalizedHeader === pattern || normalizedHeader.includes(pattern)) {
          return rule.field;
        }
      }
    }

    return ''; // No match found
  }

  UpdateFieldMapping(index: number, contactField: string): void {
    if (this.fieldMappings[index]) {
      this.fieldMappings[index].contactField = contactField;
    }
  }

  // Get available fields for a specific mapping index
  // This filters out fields that are already mapped by other columns
  GetAvailableFields(currentIndex: number): ContactField[] {
    const usedFields = this.fieldMappings
      .filter((_, i) => i !== currentIndex)
      .map(m => m.contactField)
      .filter(f => f);

    const currentSelection = this.fieldMappings[currentIndex]?.contactField || '';

    return this.contactFields.filter(field =>
      field.key === '' ||
      field.key === currentSelection ||
      !usedFields.includes(field.key)
    );
  }

  // Check if a field is already mapped by another column
  IsFieldAlreadyMapped(fieldKey: string, currentIndex: number): boolean {
    if (!fieldKey) return false; // Empty/skip is never "already mapped"

    return this.fieldMappings.some((mapping, index) =>
      index !== currentIndex && mapping.contactField === fieldKey
    );
  }

  // Name parsing (splitting a Display Name into first/middle/last, and detecting joint names like
  // "John and Jane Smith") is shared with the Companies import wizard - see
  // src/app/services/name-parser.util.ts.

  // The mappable spouse columns other than the display name (which has its own handling)
  private readonly spouseColumnFieldKeys: string[] = this.contactFields
    .map(f => f.key)
    .filter(key => key.startsWith('spouse_') && key !== 'spouse_display_name');

  // A contact row can carry the spouse's details in "Spouse ..." columns. Builds the spouse as their own contact from them
  // (or fills in the one already split off a joint name like "David and Fany Merritt"), and sets contact.spouse_display_name
  // so LinkSpouseRelationships() links the two afterwards. Returns null when the row has no spouse to create.
  // The spouse shares the household details (home phone, address, anniversary) unless the row gives them their own.
  private BuildSpouseFromColumns(contact: any, joint_name_spouse: any): any {
    const personal = ['first_name', 'middle_name', 'last_name', 'email', 'cell_phone', 'alt_phone', 'fax', 'website', 'custom_id', 'gender', 'birthday', 'sin', 'spoken_language', 'custom_data'];
    const household = ['phone_number', 'str_address', 'city', 'province', 'country', 'postal_code', 'anniversary'];
    const own = (suffix: string): string => String(contact['spouse_' + suffix] ?? '').trim();

    const has_columns = this.spouseColumnFieldKeys.some(key => String(contact[key] ?? '').trim() !== '');
    if (!has_columns) return joint_name_spouse;

    const spouse: any = joint_name_spouse ? { ...joint_name_spouse } : {
      ...contact,
      groups: [...(contact.groups || [])],
      tags: [...(contact.tags || [])],
      team_members: [...(contact.team_members || [])],
      assigned_companies: [],
      family_members: [],
      spoken_language_arr: [...(contact.spoken_language_arr || [])],
      custom_data_arr: (contact.custom_data_arr || []).map((c: any) => ({ ...c })),
      display_name: '',
      _rowNumber: contact._rowNumber + 0.5,
      _errors: [],
      _isValid: true,
    };
    if (!joint_name_spouse) {
      personal.forEach(key => { spouse[key] = ''; });
      if (this.preloadedFile) spouse._mergedContactIds = [];
    }

    personal.forEach(key => { if (own(key)) spouse[key] = own(key); });
    household.forEach(key => { spouse[key] = own(key) || (joint_name_spouse ? spouse[key] : contact[key]) || ''; });

    // The name: an explicit Spouse Display Name wins, otherwise First + Last (or whatever was split off a joint name)
    const explicit_display = String(contact.spouse_display_name ?? '').trim();
    if (explicit_display && !joint_name_spouse) {
      const parsed = parseNamesFromDisplayName(explicit_display).primary;
      if (!spouse.first_name) spouse.first_name = parsed.firstName;
      if (!spouse.middle_name && parsed.middleName) spouse.middle_name = parsed.middleName;
      if (!spouse.last_name) spouse.last_name = parsed.lastName;
    }
    spouse.display_name = joint_name_spouse?.display_name || explicit_display
      || [spouse.first_name, spouse.last_name].filter((n: string) => n).join(' ');
    if (!spouse.display_name) return joint_name_spouse;

    spouse.spouse_display_name = contact.display_name;
    contact.spouse_display_name = spouse.display_name;
    this.spouseColumnFieldKeys.forEach(key => delete spouse[key]);
    return spouse;
  }

  ProcessMappings(): void {
    this.mappedContacts = [];
    this.validationErrors = [];

    // Check if required fields are mapped
    const mappedFields = this.fieldMappings
      .filter(m => m.contactField)
      .map(m => m.contactField);

    // Whether both First and Last Name columns are mapped - when Display Name itself isn't mapped,
    // this is what triggers the fake "First + Last Name" combined display name fallback below.
    const hasFirstAndLastMapped = mappedFields.includes('first_name') && mappedFields.includes('last_name');

    // First/Last Name are derivable from Display Name (see ParseNamesFromDisplayName below), so a
    // sheet with only a combined name column still satisfies them - matches HasRequiredFieldsMapped().
    const hasDisplayNameSource = mappedFields.includes('display_name') || hasFirstAndLastMapped;
    const requiredFields = this.contactFields.filter(f => f.required);
    const missingRequired = requiredFields.filter(f => {
      if (mappedFields.includes(f.key)) return false;
      if ((f.key === 'first_name' || f.key === 'last_name') && hasDisplayNameSource) return false;
      return true;
    });

    if (missingRequired.length > 0) {
      this.validationErrors.push(
        `Missing required fields: ${missingRequired.map(f => f.label).join(', ')}`
      );
    }

    // Client Track's own ContactID column - tracked per row (see _mergedContactIds below) so that
    // when CtdAutoCleanupContacts()/MergeEmailDuplicates() consolidate rows together, we can still
    // send the backend a '~'-joined merged_ids of every legacy ContactID folded into the survivor.
    const contactIdColIndex = this.rawHeaders.findIndex(h => h.toLowerCase() === 'contactid');

    // Process each data row
    this.parsedData.forEach((row, rowIndex) => {
      const contact: any = {
        active: false,
        // Defaults to a client - the review step's per-row toggle and "Set all as non-clients" undo it.
        is_a_client: true,
        country_code: '+1',
        cell_country_code: '+1',
        alt_country_code: '+1',
        assigned_companies: [],
        family_members: [],
        team_members: [],
        groups: [],
        tags: [],
        spoken_language_arr: [],
        custom_data_arr: [],
        role_guid: '0',
        all_team_members: true,
        year_end_month: '12',
        year_end_day: '31',
        year_end_date: `${new Date().getFullYear()}-12-31`,
        // Validation properties
        _errors: [],
        _isValid: true,
        _rowNumber: rowIndex + 2, // +2 because row 1 is header and we're 0-indexed
      };

      // Set below if display_name turns out to contain a joint name ("David and Fany Merritt") -
      // pushed onto mappedContacts as its own row alongside contact once the loop body finishes.
      let spouseContact: any = null;

      if (this.preloadedFile && contactIdColIndex !== -1) {
        const rawContactId = row[contactIdColIndex];
        contact._mergedContactIds = (rawContactId !== null && rawContactId !== undefined && rawContactId !== '')
          ? [String(rawContactId)]
          : [];
      }

      // Pre-resolved groups (e.g. from the Client Track import wizard) - added before the
      // column-mapped fields below so it sees them already there.
      if (this.presetGroupsByRow[rowIndex]) contact.groups.push(...this.presetGroupsByRow[rowIndex]);

      this.fieldMappings.forEach((mapping) => {
        const colIndex = mapping.columnIndex;
        if (mapping.contactField && row[colIndex] !== undefined && row[colIndex] !== null) {
          let value = row[colIndex];

          // Convert to string and trim
          if (typeof value === 'number') {
            value = String(value);
          } else if (value instanceof Date) {
            value = value.toISOString().split('T')[0];
          } else {
            value = String(value).trim();
          }

          contact[mapping.contactField] = value;
        }
      });

      // Build custom_data_arr from user-defined custom field definitions
      this.customFieldDefinitions.forEach(def => {
        if (def.name && def.sourceColumn) {
          const colIndex = this.rawHeaders.indexOf(def.sourceColumn);
          if (colIndex !== -1) {
            const val = row[colIndex];
            contact.custom_data_arr.push({
              name: def.name,
              type: 'text',
              value: val !== null && val !== undefined ? String(val).trim() : ''
            });
          }
        }
      });

      // Multi-column Display Name builder - disabled, see displayNameParts/AddDisplayNamePart/etc
      // below and the commented-out card in import.page.html. Simplified per request: Display Name
      // column if mapped, else a fake First + Last Name combined column if both of those are mapped.
      // if (!contact.display_name && this.displayNameParts.length > 0) {
      //   const builtParts = this.displayNameParts.map(colName => {
      //     const colIndex = this.rawHeaders.indexOf(colName);
      //     if (colIndex === -1) return '';
      //     const val = row[colIndex];
      //     return val !== null && val !== undefined ? String(val).trim() : '';
      //   }).filter(v => v !== '');
      //   if (builtParts.length > 0) {
      //     contact.display_name = builtParts.join(this.displayNameSeparator ?? ' ');
      //   }
      // }

      // Fallback: no Display Name column mapped - if both First and Last Name columns are mapped,
      // treat their combined value as a fake Display Name column for this row.
      if (!contact.display_name && hasFirstAndLastMapped) {
        contact.display_name = [contact.first_name, contact.last_name]
          .filter(n => n)
          .join(' ');
      }

      // Derive first/middle/last name from display name, and split a joint name ("David and Fany
      // Merritt") into this contact (primary) plus a spawned spouse contact. Only fires on
      // display_name actually containing a joiner (and/&/et/+) - a normal single-person row falls
      // straight to the single-name fallback below and is otherwise unaffected.
      if (contact.display_name) {
        const namesResult = parseNamesFromDisplayName(contact.display_name);
        if (namesResult.spouse) {
          // A joiner was found - both halves are authoritative for identity here, since a combined
          // two-person string can never correctly be a single contact's name/first/last fields.
          if (namesResult.primary.firstName) contact.first_name = namesResult.primary.firstName;
          if (namesResult.primary.middleName) contact.middle_name = namesResult.primary.middleName;
          if (namesResult.primary.lastName) contact.last_name = namesResult.primary.lastName;
          contact.display_name = namesResult.primary.displayName;

          spouseContact = {
            ...contact,
            first_name: namesResult.spouse.firstName,
            middle_name: namesResult.spouse.middleName,
            last_name: namesResult.spouse.lastName,
            display_name: namesResult.spouse.displayName,
            spouse_display_name: contact.display_name,
            _rowNumber: contact._rowNumber + 0.5,
            _errors: [],
            _isValid: true,
          };
          if (this.preloadedFile) spouseContact._mergedContactIds = [];
          contact.spouse_display_name = spouseContact.display_name;
        } else if (!contact.first_name || !contact.last_name) {
          // No joiner - fill in whatever's still missing (sheets with no First/Last Name columns).
          if (!contact.first_name && namesResult.primary.firstName) contact.first_name = namesResult.primary.firstName;
          if (!contact.last_name && namesResult.primary.lastName) contact.last_name = namesResult.primary.lastName;
          if (!contact.middle_name && namesResult.primary.middleName) contact.middle_name = namesResult.primary.middleName;
        }
      }

      // The spouse's own columns (Spouse First Name, Spouse Email, ...) create the spouse as their own contact too
      spouseContact = this.BuildSpouseFromColumns(contact, spouseContact);
      this.spouseColumnFieldKeys.forEach(key => delete contact[key]);

      this.mappedContacts.push(contact);
      if (spouseContact) this.mappedContacts.push(spouseContact);
    });

    // Client Track exports commonly contain junk rows and duplicate/near-duplicate people (re-entered,
    // synced from multiple sources, etc.) - clean those up automatically for a CTD-sourced import
    // instead of making the user manually click through Remove/Merge for what could be hundreds of rows.
    if (this.preloadedFile) {
      this.CtdAutoCleanupContacts();
    }

    // Reset validation state - user must click Validate button
    this.isValidated = false;
    this.hasValidationErrors = false;
    this.validContactsCount = 0;
    this.invalidContactsCount = 0;
    this.showErrorsOnly = false;

    // Reset pagination
    this.currentPage = 1;

    // Detect duplicate names
    this.DetectDuplicateNames();

    // Detect email duplicates for merge option
    this.DetectEmailDuplicates();

    // Detect similar company names (e.g. "Jim's Garage" vs "Jims Garage")
    this.DetectSimilarCompanyNames();

    // Set preview (first 10 records)
    this.previewContacts = this.mappedContacts.slice(0, 10);
  }

  // Detect contacts with duplicate first + last name
  DetectDuplicateNames(): void {
    const nameCounts: { [name: string]: number[] } = {};

    // Build map of names to indices
    this.mappedContacts.forEach((contact, index) => {
      const firstName = (contact.first_name || '').toLowerCase().trim();
      const lastName = (contact.last_name || '').toLowerCase().trim();

      if (firstName || lastName) {
        const fullName = `${firstName} ${lastName}`.trim();
        if (!nameCounts[fullName]) {
          nameCounts[fullName] = [];
        }
        nameCounts[fullName].push(index);
      }
    });

    // Find duplicates
    this.duplicateNames = Object.entries(nameCounts)
      .filter(([_, indices]) => indices.length > 1)
      .map(([name, indices]) => ({ name, indices }));

    this.hasDuplicateNames = this.duplicateNames.length > 0;
  }

  // Detect contacts sharing the same email (for merge option)
  DetectEmailDuplicates(): void {
    const emailMap: { [email: string]: any[] } = {};

    this.mappedContacts.forEach(contact => {
      if (contact.email && contact.email.trim() !== '') {
        const email = contact.email.toLowerCase().trim();
        if (!emailMap[email]) emailMap[email] = [];
        emailMap[email].push(contact);
      }
    });

    this.emailDuplicateGroups = Object.entries(emailMap)
      .filter(([_, contacts]) => contacts.length > 1)
      .map(([email, contacts]) => ({ email, contacts, mergeEnabled: false }));

    this.hasInternalEmailDuplicates = this.emailDuplicateGroups.length > 0;
  }

  private GetSharedLastName(contacts: any[]): string | null {
    const uniqueLastNames = [...new Set(contacts.map(c => (c.last_name || '').trim().toLowerCase()).filter(n => n))];
    if (uniqueLastNames.length === 1) {
      // All contacts share the same last name - return original casing from first contact
      return (contacts.find(c => (c.last_name || '').trim())?.last_name || '').trim();
    }
    return null;
  }

  GetMergedDisplayName(group: { contacts: any[] }): string {
    const sharedLastName = this.GetSharedLastName(group.contacts);
    if (sharedLastName) {
      const firstNames = [...new Set(group.contacts.map(c => (c.first_name || '').trim()).filter(n => n))];
      return firstNames.join(' & ') + ' ' + sharedLastName;
    }
    return [...new Set(group.contacts.map(c => (c.display_name || '').trim()).filter(n => n))].join(' ');
  }

  GetMergedFirstName(group: { contacts: any[] }): string {
    const sharedLastName = this.GetSharedLastName(group.contacts);
    if (sharedLastName) {
      const firstNames = [...new Set(group.contacts.map(c => (c.first_name || '').trim()).filter(n => n))];
      return firstNames.join(' & ');
    }
    return [...new Set(group.contacts.map(c => (c.first_name || '').trim()).filter(n => n))].join(' ');
  }

  GetMergedLastName(group: { contacts: any[] }): string {
    const sharedLastName = this.GetSharedLastName(group.contacts);
    if (sharedLastName) {
      return sharedLastName;
    }
    return [...new Set(group.contacts.map(c => (c.last_name || '').trim()).filter(n => n))].join(' ');
  }

  get hasMergeEnabled(): boolean {
    return this.emailDuplicateGroups.some(g => g.mergeEnabled);
  }

  async MergeAllEmailDuplicates(): Promise<void> {
    const alert = await this.alertController.create({
      header: 'Merge Duplicates',
      message: `Are you sure you want to merge all ${this.emailDuplicateGroups.length} duplicate email group(s) into single contacts? This action cannot be undone.`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Merge',
          handler: () => {
            this.emailDuplicateGroups.forEach(g => g.mergeEnabled = true);
            this.MergeEmailDuplicates();
          }
        }
      ]
    });
    await alert.present();
  }

  // Apply merge for all toggled-on groups
  MergeEmailDuplicates(): void {
    const groupsToMerge = this.emailDuplicateGroups.filter(g => g.mergeEnabled);

    for (const group of groupsToMerge) {
      const mergedDisplayName = this.GetMergedDisplayName(group);
      const mergedFirstName = this.GetMergedFirstName(group);
      const mergedLastName = this.GetMergedLastName(group);

      const mergedContact = { ...group.contacts[0] };
      mergedContact.first_name = mergedFirstName;
      mergedContact.last_name = mergedLastName;
      mergedContact.display_name = mergedDisplayName;

      // Fill any empty fields from the other contacts in the group
      const fieldsToFill = ['phone_number', 'cell_phone', 'alt_phone', 'str_address', 'city', 'province', 'country', 'postal_code', 'website', 'custom_id', 'gender', 'birthday', 'anniversary', 'sin', 'spoken_language', 'custom_data'];
      for (const field of fieldsToFill) {
        if (!mergedContact[field] || mergedContact[field] === '') {
          for (const contact of group.contacts.slice(1)) {
            if (contact[field] && contact[field] !== '') {
              mergedContact[field] = contact[field];
              break;
            }
          }
        }
      }

      // Client Track imports only - track every legacy ContactID folded into this merge (see
      // ProcessMappings/CtdPickMostRecentContact), so it can be sent to the backend as merged_ids.
      mergedContact._mergedContactIds = Array.from(new Set(
        group.contacts.reduce((ids: string[], c: any) => ids.concat(c._mergedContactIds || []), [] as string[])
      ));

      // Remove duplicate contacts (all but the first)
      const indicesToRemove = group.contacts
        .slice(1)
        .map(c => this.mappedContacts.indexOf(c))
        .filter(i => i !== -1)
        .sort((a, b) => b - a);

      indicesToRemove.forEach(i => this.mappedContacts.splice(i, 1));

      // Update the first contact
      const firstIdx = this.mappedContacts.indexOf(group.contacts[0]);
      if (firstIdx !== -1) {
        this.mappedContacts[firstIdx] = mergedContact;
      }
    }

    this.DetectEmailDuplicates();
    this.DetectDuplicateNames();
    this.isValidated = false;
    this.currentPage = 1;
  }

  // Remove duplicate names (keep first occurrence)
  RemoveDuplicateNames(): void {
    // Get all indices to remove (all but first occurrence of each duplicate)
    const indicesToRemove = new Set<number>();

    this.duplicateNames.forEach(dup => {
      // Keep the first one, remove the rest
      dup.indices.slice(1).forEach(index => indicesToRemove.add(index));
    });

    // Remove from highest index to lowest to avoid index shifting issues
    const sortedIndices = Array.from(indicesToRemove).sort((a, b) => b - a);
    sortedIndices.forEach(index => {
      this.mappedContacts.splice(index, 1);
    });

    // Re-detect duplicates (should be none now)
    this.DetectDuplicateNames();

    // Invalidate validation
    this.isValidated = false;

    // Reset to first page
    this.currentPage = 1;
  }

  // Remove contacts with emails that already exist in the portal
  RemoveExistingEmailConflicts(): void {
    // Get all indices to remove
    const indicesToRemove = new Set<number>();

    this.existingEmailConflicts.forEach(conflict => {
      const index = this.mappedContacts.findIndex(c => c._rowNumber === conflict.rowNumber);
      if (index !== -1) {
        indicesToRemove.add(index);
      }
    });

    // Remove from highest index to lowest to avoid index shifting issues
    const sortedIndices = Array.from(indicesToRemove).sort((a, b) => b - a);
    sortedIndices.forEach(index => {
      this.mappedContacts.splice(index, 1);
    });

    // Clear the conflicts
    this.existingEmailConflicts = [];
    this.hasExistingEmailConflicts = false;

    // Invalidate validation
    this.isValidated = false;

    // Reset to first page
    this.currentPage = 1;
  }

  async ValidateContacts(): Promise<void> {
    this.isValidating = true;
    this.showErrorsOnly = false; // Start each fresh validation run unfiltered
    this.currentPage = 1;
    this.validationErrors = [];
    this.hasValidationErrors = false;
    this.validContactsCount = 0;
    this.invalidContactsCount = 0;
    this.existingEmailConflicts = [];
    this.hasExistingEmailConflicts = false;
    this.existingNameMatches = [];
    this.hasExistingNameMatches = false;
    this.exactSystemDuplicates = [];
    this.hasExactSystemDuplicates = false;

    // Fetch existing contacts to check for email conflicts and name matches
    try {
      const existingContactsResponse: any = await this.apiService.get('clients');
      if (existingContactsResponse.success && existingContactsResponse.data) {
        this.existingEmails = existingContactsResponse.data
          .filter((c: any) => c.email && c.email.trim() !== '')
          .map((c: any) => c.email.toLowerCase().trim());
        
        // Build list of existing client names for matching
        this.existingClientNames = existingContactsResponse.data.map((c: any) => ({
          fullName: `${(c.first_name || '').toLowerCase().trim()} ${(c.last_name || '').toLowerCase().trim()}`.trim(),
          displayName: (c.display_name || '').toLowerCase().trim()
        }));

        // Build list of existing display name + email pairs
        this.existingNameEmailPairs = existingContactsResponse.data
          .filter((c: any) => c.display_name && c.email)
          .map((c: any) => ({
            displayName: (c.display_name || '').toLowerCase().trim(),
            email: (c.email || '').toLowerCase().trim()
          }));
      }
    } catch (error) {
      console.error('Error fetching existing contacts:', error);
      this.existingEmails = [];
      this.existingClientNames = [];
      this.existingNameEmailPairs = [];
    }

    // Validate each contact
    this.mappedContacts.forEach((contact) => {
      contact._errors = [];
      contact._isValid = true;
      contact._emailInvalid = false;
      contact._emailDuplicate = false;
      contact._emailRequired = false;
      contact._emailExists = false;
      contact._nameMissing = false;
      contact._nameMatchWarning = false;
      contact._warnings = [];

      // Check for required name information
      if (!contact.first_name && !contact.last_name && !contact.display_name) {
        contact._errors.push('Missing name information (first name, last name, or display name required)');
        contact._nameMissing = true;
      }

      if (!contact.first_name) {
        contact._errors.push('First name is required');
      }

      if (!contact.last_name) {
        contact._errors.push('Last name is required');
      }

      if (!contact.display_name) {
        contact._errors.push('Display name is required');
      }

      // Validate email format if provided
      if (contact.email && contact.email.trim() !== '' && !contact._emailInvalid) {
        if (!this.globalService.validateEmail(contact.email)) {
          contact._errors.push('Invalid email format');
          contact._emailInvalid = true;
        }

      }

      // Check for matching names with existing clients (warning only)
      const importFirstName = (contact.first_name || '').toLowerCase().trim();
      const importLastName = (contact.last_name || '').toLowerCase().trim();
      const importFullName = `${importFirstName} ${importLastName}`.trim();
      const importDisplayName = (contact.display_name || '').toLowerCase().trim();

      // Check if first + last name matches any existing client
      const fullNameMatch = this.existingClientNames.find(c => c.fullName && c.fullName === importFullName && importFullName !== '');
      if (fullNameMatch) {
        contact._nameMatchWarning = true;
        contact._warnings.push(`A client with the name "${contact.first_name} ${contact.last_name}" already exists`);
        this.existingNameMatches.push({
          rowNumber: contact._rowNumber,
          displayName: contact.display_name || `${contact.first_name} ${contact.last_name}`.trim() || 'Unknown',
          matchType: 'First + Last Name',
          matchedName: `${contact.first_name} ${contact.last_name}`
        });
      }

      // Check if display name matches any existing client
      const displayNameMatch = this.existingClientNames.find(c => c.displayName && c.displayName === importDisplayName && importDisplayName !== '');
      if (displayNameMatch && !fullNameMatch) {
        contact._nameMatchWarning = true;
        contact._warnings.push(`A client with the display name "${contact.display_name}" already exists`);
        this.existingNameMatches.push({
          rowNumber: contact._rowNumber,
          displayName: contact.display_name,
          matchType: 'Display Name',
          matchedName: contact.display_name
        });
      }

      // Check if display name + email both match an existing client (strong duplicate warning)
      if (importDisplayName && contact.email && contact.email.trim() !== '') {
        const importEmail = contact.email.toLowerCase().trim();
        const nameEmailMatch = this.existingNameEmailPairs.find(
          p => p.displayName === importDisplayName && p.email === importEmail
        );
        if (nameEmailMatch) {
          contact._nameMatchWarning = true;
          contact._warnings.push(`A contact with the name "${contact.display_name}" and email "${contact.email}" already exists in the system`);
          if (!this.existingNameMatches.find(m => m.rowNumber === contact._rowNumber && m.matchType === 'Name + Email')) {
            this.existingNameMatches.push({
              rowNumber: contact._rowNumber,
              displayName: contact.display_name,
              matchType: 'Name + Email',
              matchedName: contact.display_name
            });
          }
          this.exactSystemDuplicates.push({
            rowNumber: contact._rowNumber,
            displayName: contact.display_name,
            email: contact.email
          });
        }
      }

      // Update validity status
      if (contact._errors.length > 0) {
        contact._isValid = false;
        this.invalidContactsCount++;
        this.hasValidationErrors = true;
      } else {
        this.validContactsCount++;
      }
    });

    // Build summary validation errors
    if (this.invalidContactsCount > 0) {
      this.validationErrors.push(`${this.invalidContactsCount} contact(s) have validation errors`);
    }

    // Check for existing name matches (warning only, not an error)
    if (this.existingNameMatches.length > 0) {
      this.hasExistingNameMatches = true;
    }

    // Flag exact system duplicates
    if (this.exactSystemDuplicates.length > 0) {
      this.hasExactSystemDuplicates = true;
    }

    this.isValidated = true;
    this.isValidating = false;
  }

  // Remove contacts that exactly match an existing system record (display_name + email)
  RemoveExactSystemDuplicates(): void {
    const rowsToRemove = new Set(this.exactSystemDuplicates.map(d => d.rowNumber));
    this.mappedContacts = this.mappedContacts.filter(c => !rowsToRemove.has(c._rowNumber));
    this.exactSystemDuplicates = [];
    this.hasExactSystemDuplicates = false;
    this.isValidated = false;
    this.DetectDuplicateNames();
    this.currentPage = 1;
  }

  // Remove contacts whose name matches an existing client already in the system
  RemoveExistingNameMatches(): void {
    const rowsToRemove = new Set(this.existingNameMatches.map(m => m.rowNumber));
    this.mappedContacts = this.mappedContacts.filter(c => !rowsToRemove.has(c._rowNumber));
    this.existingNameMatches = [];
    this.hasExistingNameMatches = false;
    // Those rows may have also been counted as exact system duplicates - drop any that just left.
    this.exactSystemDuplicates = this.exactSystemDuplicates.filter(d => !rowsToRemove.has(d.rowNumber));
    this.hasExactSystemDuplicates = this.exactSystemDuplicates.length > 0;
    this.isValidated = false;
    this.DetectDuplicateNames();
    this.currentPage = 1;
  }

  // Mark data as changed (invalidates previous validation)
  OnDataChanged(): void {
    this.isValidated = false;
  }

  // Bulk toggle for setting all contacts as clients or non-clients
  async SetAllAsClients(isClient: boolean): Promise<void> {
    const selectedCount = this.GetSelectedContactsCount();
    if (selectedCount > 0) {
      this.mappedContacts.filter(c => c._selected).forEach(contact => {
        contact.is_a_client = isClient;
      });
      this.OnDataChanged();
      return;
    }
    const alert = await this.alertController.create({
      header: 'Mark as Client',
      message: `Are you sure you want to set all contacts as ${isClient ? 'clients' : 'non-clients'}?`,
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: () => {
            this.mappedContacts.forEach(contact => {
              contact.is_a_client = isClient;
            });
            this.OnDataChanged();
          }
        }
      ]
    });
    await alert.present();
  }

  async SetAllPortalAccess(hasAccess: boolean): Promise<void> {
    const selectedCount = this.GetSelectedContactsCount();
    if (selectedCount > 0) {
      if (hasAccess) {
        const alert = await this.alertController.create({
          header: 'Grant Portal Access',
          message: `Are you sure you want to grant portal access to the ${selectedCount} selected contact(s)? This will send each of them an email with login credentials.`,
          buttons: [
            { text: 'Cancel', role: 'cancel' },
            {
              text: 'Confirm',
              handler: () => {
                this.mappedContacts.filter(c => c._selected).forEach(contact => {
                  contact.active = hasAccess;
                });
                this.OnDataChanged();
              }
            }
          ]
        });
        await alert.present();
        return;
      }
      this.mappedContacts.filter(c => c._selected).forEach(contact => {
        contact.active = hasAccess;
      });
      this.OnDataChanged();
      return;
    }
    if (hasAccess) {
      const alert = await this.alertController.create({
        header: 'Grant Portal Access',
        message: 'Are you sure you want to grant portal access to all contacts? This will send each of them an email with login credentials.',
        buttons: [
          { text: 'Cancel', role: 'cancel' },
          {
            text: 'Confirm',
            handler: () => {
              this.mappedContacts.forEach(contact => {
                contact.active = hasAccess;
              });
              this.OnDataChanged();
            }
          }
        ]
      });
      await alert.present();
      return;
    }
    const alert = await this.alertController.create({
      header: 'Revoke Portal Access',
      message: 'Are you sure you want to revoke portal access for all contacts?',
      buttons: [
        { text: 'Cancel', role: 'cancel' },
        {
          text: 'Confirm',
          handler: () => {
            this.mappedContacts.forEach(contact => {
              contact.active = hasAccess;
            });
            this.OnDataChanged();
          }
        }
      ]
    });
    await alert.present();
  }

  // Normalize common company suffixes to a canonical form (applied after basic normalization)
  private NormalizeCompanySuffixes(name: string): string {
    const replacements: [RegExp, string][] = [
      // Multi-word suffixes first (most specific)
      [/\bprofessional corporation$/, 'prof corp'],
      [/\bprofessional corp$/, 'prof corp'],
      [/\blimited liability partnership$/, 'llp'],
      [/\blimited liability company$/, 'llc'],
      // Single-word suffixes
      [/\bincorporated$/, 'inc'],
      [/\bcorporation$/, 'corp'],
      [/\bcompany$/, 'co'],
      [/\blimited$/, 'ltd'],
      [/\blld$/, 'ltd'],
      [/\bpartnership$/, 'partners'],
    ];
    let result = name;
    for (const [pattern, replacement] of replacements) {
      result = result.replace(pattern, replacement);
    }
    return result.trim();
  }

  // Normalize a name for similarity comparison: lowercase, normalize & → and, strip punctuation,
  // collapse spaces, then canonicalize common company suffixes (Inc/Inc., Corp/Corporation, etc.)
  private NormalizeCompanyName(name: string): string {
    const normalized = name
      .toLowerCase()
      .replace(/&/g, ' and ')      // normalize ampersand before stripping punctuation
      .replace(/[^a-z0-9\s]/g, '') // strip remaining punctuation
      .replace(/\s+/g, ' ')        // collapse spaces
      .trim();
    return this.NormalizeCompanySuffixes(normalized);
  }

  // Detect display names that are similar but not identical (e.g. "Jim's Garage" vs "Jims Garage")
  DetectSimilarCompanyNames(): void {
    const nameMap: { [normalized: string]: { [original: string]: any[] } } = {};

    this.mappedContacts.forEach(contact => {
      const displayName = (contact.display_name || '').trim();
      if (!displayName) return;
      const normalized = this.NormalizeCompanyName(displayName);
      if (!normalized) return;
      if (!nameMap[normalized]) nameMap[normalized] = {};
      if (!nameMap[normalized][displayName]) nameMap[normalized][displayName] = [];
      nameMap[normalized][displayName].push(contact);
    });

    this.similarNameGroups = Object.entries(nameMap)
      .filter(([_, byName]) => Object.keys(byName).length > 1)
      .map(([_, contactsByName]) => {
        const names = Object.keys(contactsByName);
        return {
          names,
          contactsByName,
          selectedName: names[0],
          customName: '',
          useCustomName: false,
          dismissed: false
        };
      });

    this.hasSimilarNameGroups = this.similarNameGroups.length > 0;
  }

  GetVisibleSimilarNameGroups(): SimilarNameGroup[] {
    return this.similarNameGroups.filter(g => !g.dismissed);
  }

  // Apply the chosen canonical name to all contacts in a similar-name group
  ApplySimilarNameGroupChange(group: SimilarNameGroup): void {
    const chosenName = group.useCustomName ? (group.customName || '').trim() : group.selectedName;
    if (!chosenName) return;

    group.names.forEach(name => {
      (group.contactsByName[name] || []).forEach(contact => {
        contact.display_name = chosenName;
      });
    });

    this.isValidated = false;
    this.DetectDuplicateNames();
    this.DetectSimilarCompanyNames();
    this.currentPage = 1;
  }

  // Dismiss a similar-name group without making any changes
  DismissSimilarNameGroup(group: SimilarNameGroup): void {
    group.dismissed = true;
    this.hasSimilarNameGroups = this.similarNameGroups.some(g => !g.dismissed);
  }

  // ContactsModifiedStamp is excluded from the primary field-mapping list (see manage-firm.page.ts's
  // ctdExcludedColumnNames) so it never lands on the mapped contact object itself - read it straight
  // from the raw parsed row instead. Contacts missing the column (or non-CTD imports) sort as "oldest".
  private CtdGetModifiedTimestamp(contact: any): number {
    const colIndex = this.rawHeaders.findIndex(h => h.toLowerCase() === 'contactsmodifiedstamp');
    if (colIndex === -1) return 0;
    const row = this.parsedData[contact._rowNumber - 2];
    const raw = row ? row[colIndex] : null;
    if (!raw) return 0;
    const time = new Date(raw).getTime();
    return isNaN(time) ? 0 : time;
  }

  // Picks which contact to keep out of a group that all represent the same real person/company -
  // never blends field data between them, the discarded ones are simply dropped as-is. Prefers a
  // contact with an email (if any in the group have one), then whichever of that preferred set has
  // the latest ContactsModifiedStamp (see CtdGetModifiedTimestamp). Every source ContactID is still
  // recorded on the survivor's _mergedContactIds (sent to the backend as merged_ids on import - see
  // ImportContacts) even though nothing else about the discarded rows carries over.
  private CtdPickMostRecentContact(contacts: any[]): any {
    const withEmail = contacts.filter(c => c.email && c.email.trim() !== '');
    const candidates = withEmail.length > 0 ? withEmail : contacts;

    const survivor = [...candidates].sort((a, b) =>
      this.CtdGetModifiedTimestamp(b) - this.CtdGetModifiedTimestamp(a)
    )[0];

    survivor._mergedContactIds = Array.from(new Set(
      contacts.reduce((ids: string[], c: any) => ids.concat(c._mergedContactIds || []), [] as string[])
    ));
    return survivor;
  }

  // Removes every contact in group except survivor from mappedContacts (descending index order so
  // splicing doesn't shift indices out from under itself).
  private CtdRemoveMergedAwayContacts(group: any[], survivor: any): void {
    const indicesToRemove = group
      .filter(c => c !== survivor)
      .map(c => this.mappedContacts.indexOf(c))
      .filter(i => i !== -1)
      .sort((a, b) => b - a);
    indicesToRemove.forEach(i => this.mappedContacts.splice(i, 1));
  }

  // CTD-only auto-cleanup: drops rows with nothing usable to import, then resolves exact and
  // near-duplicate names down to one row each via CtdPickMostRecentContact - rather than requiring
  // the user to manually click through Remove/Merge for what could be hundreds of rows.
  private CtdAutoCleanupContacts(): void {
    // Junk rows: no first/last/display name and no email - nothing usable to import.
    this.mappedContacts = this.mappedContacts.filter(c =>
      c.first_name || c.last_name || c.display_name || c.email
    );

    // Exact first+last name duplicates - every group's contacts are resolved from one pristine
    // snapshot up front (not re-read from mappedContacts by index mid-loop), since removing an
    // earlier group shifts array positions out from under any index captured before it ran.
    this.DetectDuplicateNames();
    const duplicateGroups = this.duplicateNames.map(dup => dup.indices.map(i => this.mappedContacts[i]));
    duplicateGroups.forEach(group => {
      const survivor = this.CtdPickMostRecentContact(group);
      this.CtdRemoveMergedAwayContacts(group, survivor);
    });

    // Similar-but-not-identical display names (e.g. "Jim's Garage" vs "Jims Garage") - same pick
    // logic, using the kept contact's own spelling as the resulting display_name. Re-detected fresh
    // each iteration since resolving one group changes what's left to detect - contactsByName holds
    // live object references (not indices), so this loop isn't subject to the staleness above.
    this.DetectSimilarCompanyNames();
    while (this.similarNameGroups.length > 0) {
      const group = this.similarNameGroups[0];
      const allContacts = group.names.reduce((acc: any[], name: string) => acc.concat(group.contactsByName[name] || []), [] as any[]);
      const survivor = this.CtdPickMostRecentContact(allContacts);
      this.CtdRemoveMergedAwayContacts(allContacts, survivor);
      this.DetectSimilarCompanyNames();
    }
  }

  RemoveFile(event: Event): void {
    event.stopPropagation();
    this.uploadedFile = null;
    this.filePreviewData = null;
    this.uploadError = null;
    this.parsedData = [];
    this.rawHeaders = [];
    this.fieldMappings = [];
    this.dataQualityIssues = [];
    this.hasDataQualityIssues = false;
    this.similarNameGroups = [];
    this.hasSimilarNameGroups = false;
    this.displayNameParts = [];
    this.displayNameSeparator = ' ';
    this.displayNameColumnToAdd = '';
    this.customFieldDefinitions = [];
  }

  FormatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  GetFieldLabel(fieldKey: string): string {
    const field = this.contactFields.find(f => f.key === fieldKey);
    return field ? field.label : fieldKey;
  }

  GetMappedFieldsCount(): number {
    return this.fieldMappings.filter(m => m.contactField).length;
  }

  HasRequiredFieldsMapped(): boolean {
    const mappedFields = this.fieldMappings
      .filter(m => m.contactField)
      .map(m => m.contactField);

    const hasDisplayNameSource = mappedFields.includes('display_name')
      || (mappedFields.includes('first_name') && mappedFields.includes('last_name'));

    const requiredFields = this.contactFields.filter(f => f.required);
    return requiredFields.every(f => {
      if (f.key === 'display_name') return hasDisplayNameSource;
      // First/Last Name can be derived from Display Name (see ParseNamesFromDisplayName in
      // ProcessMappings) when the sheet doesn't have dedicated columns for them - don't block
      // progress past the mapping step just because those two columns don't exist.
      if (f.key === 'first_name' || f.key === 'last_name') {
        return mappedFields.includes(f.key) || hasDisplayNameSource;
      }
      return mappedFields.includes(f.key);
    });
  }

  GetUnmappedRequiredFields(): string[] {
    const mappedFields = this.fieldMappings
      .filter(m => m.contactField)
      .map(m => m.contactField);

    const requiredFields = this.contactFields.filter(f => f.required);
    return requiredFields
      .filter(f => {
        if (f.key === 'display_name' && mappedFields.includes('first_name') && mappedFields.includes('last_name')) return false;
        return !mappedFields.includes(f.key);
      })
      .map(f => f.label);
  }

  // Display name builder methods
  IsDisplayNameMapped(): boolean {
    return this.fieldMappings.some(m => m.contactField === 'display_name');
  }

  GetColumnsNotInDisplayNameParts(): string[] {
    return this.rawHeaders.filter(h => !this.displayNameParts.includes(h));
  }

  AddCustomImportField(): void {
    this.customFieldDefinitions.push({ name: '', sourceColumn: '' });
  }

  RemoveCustomImportField(index: number): void {
    this.customFieldDefinitions.splice(index, 1);
  }

  // Columns not currently used by a primary field mapping and not already claimed by a custom field
  // definition - includes columns hidden from the primary list entirely (see excludeFromMapping).
  GetUnmappedColumns(): string[] {
    const mappedColumns = new Set(this.fieldMappings.filter(m => m.contactField).map(m => m.excelColumn));
    const usedAsCustomField = new Set(this.customFieldDefinitions.filter(d => d.sourceColumn).map(d => d.sourceColumn));
    return this.rawHeaders.filter(h => !mappedColumns.has(h) && !usedAsCustomField.has(h));
  }

  MapAllUnmappedFields(): void {
    this.GetUnmappedColumns().forEach(col => {
      this.customFieldDefinitions.push({ name: col, sourceColumn: col });
    });
  }

  AddDisplayNamePart(col: string): void {
    if (col && !this.displayNameParts.includes(col)) {
      this.displayNameParts.push(col);
      this.displayNameColumnToAdd = '';
    }
  }

  RemoveDisplayNamePart(index: number): void {
    this.displayNameParts.splice(index, 1);
  }

  MoveDisplayNamePartUp(index: number): void {
    if (index > 0) {
      const temp = this.displayNameParts[index];
      this.displayNameParts[index] = this.displayNameParts[index - 1];
      this.displayNameParts[index - 1] = temp;
    }
  }

  MoveDisplayNamePartDown(index: number): void {
    if (index < this.displayNameParts.length - 1) {
      const temp = this.displayNameParts[index];
      this.displayNameParts[index] = this.displayNameParts[index + 1];
      this.displayNameParts[index + 1] = temp;
    }
  }

  GetDisplayNamePreview(): string {
    if (this.displayNameParts.length === 0 || this.parsedData.length === 0) return '';
    const firstRow = this.parsedData[0];
    const parts = this.displayNameParts.map(colName => {
      const colIndex = this.rawHeaders.indexOf(colName);
      if (colIndex === -1) return '';
      const val = firstRow[colIndex];
      return val !== null && val !== undefined ? String(val).trim() : '';
    }).filter(v => v !== '');
    return parts.join(this.displayNameSeparator ?? ' ');
  }

  // Contacts actually shown in the table/cards - every mapped contact normally, or just the ones
  // with validation errors when showErrorsOnly is toggled on. Deliberately NOT gated on isValidated -
  // editing a contact flips isValidated to false immediately (see OnDataChanged), but _isValid itself
  // stays put until the next explicit Validate click, so staying filtered on it (rather than snapping
  // back to "All" on every keystroke) is what lets you actually work through the error list.
  get filteredContacts(): any[] {
    return this.showErrorsOnly
      ? this.mappedContacts.filter(c => !c._isValid)
      : this.mappedContacts;
  }

  SetShowErrorsOnly(value: boolean): void {
    this.showErrorsOnly = value;
    this.currentPage = 1;
  }

  // Pagination methods
  get paginatedContacts(): any[] {
    const startIndex = (this.currentPage - 1) * this.pageSize;
    return this.filteredContacts.slice(startIndex, startIndex + this.pageSize);
  }

  get totalPages(): number {
    return Math.ceil(this.filteredContacts.length / this.pageSize);
  }

  get startRecord(): number {
    return (this.currentPage - 1) * this.pageSize + 1;
  }

  get endRecord(): number {
    return Math.min(this.currentPage * this.pageSize, this.filteredContacts.length);
  }

  GoToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages) {
      this.currentPage = page;
    }
  }

  NextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
    }
  }

  PreviousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
    }
  }

  // Get array of page numbers for pagination display
  GetPageNumbers(): number[] {
    const pages: number[] = [];
    const maxPagesToShow = 5;
    let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
    let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

    // Adjust start if we're near the end
    if (endPage - startPage + 1 < maxPagesToShow) {
      startPage = Math.max(1, endPage - maxPagesToShow + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }
    return pages;
  }

  // Selection methods for editable table
  GetSelectedContactsCount(): number {
    return this.mappedContacts.filter(c => c._selected).length;
  }

  AreAllContactsSelected(): boolean {
    return this.mappedContacts.length > 0 && this.mappedContacts.every(c => c._selected);
  }

  ToggleSelectAllContacts(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    this.mappedContacts.forEach(contact => contact._selected = checked);
  }

  DeleteContact(index: number): void {
    // Look up by object reference rather than page math - paginatedContacts is a slice of
    // filteredContacts, not mappedContacts directly, once showErrorsOnly is in play.
    const contact = this.paginatedContacts[index];
    const actualIndex = contact ? this.mappedContacts.indexOf(contact) : -1;
    if (actualIndex !== -1) {
      this.mappedContacts.splice(actualIndex, 1);
      this.isValidated = false; // Invalidate - user must re-validate
      this.DetectDuplicateNames(); // Re-check duplicates

      // Adjust current page if necessary
      if (this.currentPage > this.totalPages && this.totalPages > 0) {
        this.currentPage = this.totalPages;
      }
    }
  }

  DeleteSelectedContacts(): void {
    this.mappedContacts = this.mappedContacts.filter(c => !c._selected);
    this.isValidated = false; // Invalidate - user must re-validate
    this.DetectDuplicateNames(); // Re-check duplicates

    // Adjust current page if necessary
    if (this.currentPage > this.totalPages && this.totalPages > 0) {
      this.currentPage = this.totalPages;
    } else if (this.totalPages === 0) {
      this.currentPage = 1;
    }
  }

  async ImportContacts() {
    const contactsToImport = this.mappedContacts.map(contact => {
      const { _selected, _errors, _isValid, _rowNumber, _emailInvalid, _emailDuplicate, _emailRequired, _nameMissing, _mergedContactIds, ...cleanContact } = contact;

      // Client Track imports only - '~'-joined legacy ContactID(s) this client was created/merged
      // from (see ProcessMappings/CtdPickMostRecentContact/MergeEmailDuplicates).
      if (_mergedContactIds && _mergedContactIds.length > 0) {
        cleanContact.merged_ids = _mergedContactIds.join('~');
      }

      return { ...cleanContact, _rowNumber, _selected };
    });

    const importErrors: { rowNumber: number; displayName: string; error: string }[] = [];
    const successfulImports: any[] = [];
    let importedCount = 0;

    await this.globalService.presentLoading(`Importing contacts (0/${contactsToImport.length})...`, 3600000);

    for (const contact of contactsToImport) {
      try {
        // Update loading message
        await this.globalService.presentLoading(`Importing contacts (${importedCount + 1}/${contactsToImport.length})...`, 3600000);

        // Step 1: Create the client (strip internal tracking fields before posting)
        const { _rowNumber, _selected, ...contactPayload } = contact;
        const clientCreate: any = await this.apiService.post('clients', contactPayload);

        if (!clientCreate.success) {
          importErrors.push({
            rowNumber: contact._rowNumber,
            displayName: contact.display_name || `${contact.first_name} ${contact.last_name}`.trim() || 'Unknown',
            error: clientCreate.message || 'Failed to create contact'
          });
          continue;
        }

        const clientGuid = clientCreate.data[0].guid;

        // Step 2: Update client details
        if (contact.spoken_language_arr && contact.spoken_language_arr.length > 0) {
          contactPayload.spoken_language = contact.spoken_language_arr
            .map((lang: any) => typeof lang === 'string' ? lang : lang.name || lang.value || '')
            .join('~');
        }

        if (contactPayload.custom_data_arr && contactPayload.custom_data_arr.length > 0) {
          contactPayload.custom_data = JSON.stringify(contactPayload.custom_data_arr);
        }

        await this.apiService.put(`clients/${clientGuid}`, contactPayload);

        // Step 3: Assign tags if any
        if (contact.tags && contact.tags.length > 0) {
          for (const tag of contact.tags) {
            await this.apiService.post(`clients/tags/${clientGuid}`, tag);
          }
        }

        // Step 4: Assign team members if any
        if (contact.team_members && contact.team_members.length > 0) {
          for (const teamMember of contact.team_members) {
            await this.apiService.post(`clients/team_members/${clientGuid}`, teamMember);
          }
        }

        // Step 5: Assign family members if any
        if (contact.family_members && contact.family_members.length > 0) {
          for (const familyMember of contact.family_members) {
            await this.apiService.post(`clients/family_members/${clientGuid}`, familyMember);
          }
        }

        // Step 6: Assign groups if any
        if (contact.groups && contact.groups.length > 0) {
          for (const group of contact.groups) {
            await this.apiService.post(`groups/clients/${clientGuid}`, group);
          }
        }

        // Step 6b: Assign bulk import groups to all imported contacts
        for (const group of this.selectedGroups) {
          const alreadyAssigned = contact.groups && contact.groups.some((g: any) => g.guid === group.guid);
          if (!alreadyAssigned) {
            await this.apiService.post(`groups/clients/${clientGuid}`, group);
          }
        }

        // Step 6c: Assign bulk import tags to all imported contacts
        for (const tag of this.selectedTags) {
          const alreadyAssigned = contact.tags && contact.tags.some((t: any) => t.guid === tag.guid);
          if (!alreadyAssigned) {
            await this.apiService.post(`clients/tags/${clientGuid}`, tag);
          }
        }

        // Step 7: Assign to companies if any
        if (contact.assigned_companies && contact.assigned_companies.length > 0) {
          for (const company of contact.assigned_companies) {
            const postData = {
              guid: clientGuid,
              role_guid: company.role_guid
            };
            await this.apiService.post(`companies/clients/${company.guid}`, postData);
          }
        }

        // spouse_display_name isn't a real client field (the backend just ignores it), so it
        // never comes back on clientCreate.data[0] - carry the original value through here so
        // LinkSpouseRelationships() below still has it to work with.
        successfulImports.push({ ...clientCreate.data[0], spouse_display_name: contact.spouse_display_name });
        importedCount++;

      } catch (error: any) {
        importErrors.push({
          rowNumber: contact._rowNumber,
          displayName: contact.display_name || `${contact.first_name} ${contact.last_name}`.trim() || 'Unknown',
          error: error.message || 'An unexpected error occurred'
        });
      }
    }

    // Link "Spouse Display Name" family relationships now that every contact in this batch
    // actually exists to be linked against.
    let spouseLinkResult = { linkedCount: 0, unmatched: [] as string[], ambiguous: [] as string[] };
    if (successfulImports.some(c => c.spouse_display_name && c.spouse_display_name.trim())) {
      await this.globalService.presentLoading('Linking spouse relationships...', 3600000);
      spouseLinkResult = await this.LinkSpouseRelationships(successfulImports);
    }

    this.globalService.dismissLoading();

    // Show summary message
    let summary = importErrors.length > 0
      ? `Imported ${successfulImports.length} of ${contactsToImport.length} contacts. ${importErrors.length} failed.`
      : `Successfully imported ${successfulImports.length} contact(s).`;
    if (spouseLinkResult.linkedCount > 0) summary += ` Linked ${spouseLinkResult.linkedCount} spouse relationship(s).`;
    if (spouseLinkResult.unmatched.length > 0) summary += ` Could not find a matching contact for ${spouseLinkResult.unmatched.length} spouse name(s).`;
    if (spouseLinkResult.ambiguous.length > 0) summary += ` ${spouseLinkResult.ambiguous.length} spouse name(s) matched more than one contact and were skipped.`;
    await this.globalService.presentToastMessage(summary, successfulImports.length > 0);

    await this.modalController.dismiss({
      imported: true,
      contacts: successfulImports,
      errors: importErrors,
      totalAttempted: contactsToImport.length,
      successCount: successfulImports.length,
      errorCount: importErrors.length
    });
  }

  // Links a "Spouse" family relationship for every just-imported contact that had a "Spouse
  // Display Name" column mapped, matching it by exact (case-insensitive) display_name against
  // the firm's full client list - not just this import batch, so a spouse already in the system
  // still gets linked. One clients/family_members/{guid} call creates both directions (see
  // Users/Clients.cs's CreateFamilyLink), so each pair only needs one request; a sorted-guid pair
  // key stops a mutual "spouse of each other" pair from being linked twice. Mirrors
  // manage-firm.page.ts's CtdLinkFamilyRelationships (same relationship-linking pattern, for the
  // CTD import instead).
  async LinkSpouseRelationships(successfulImports: any[]): Promise<{ linkedCount: number; unmatched: string[]; ambiguous: string[] }> {
    const result = { linkedCount: 0, unmatched: [] as string[], ambiguous: [] as string[] };
    const contactsWithSpouse = successfulImports.filter(c => c.spouse_display_name && c.spouse_display_name.trim());
    if (contactsWithSpouse.length === 0) return result;

    const allClientsData: any = await this.apiService.get('clients');
    const allClients: any[] = (allClientsData?.success && allClientsData.data) ? allClientsData.data : [];
    const normalize = (s: string) => (s || '').trim().toLowerCase();

    const linkedPairKeys = new Set<string>();
    for (const contact of contactsWithSpouse) {
      const spouseName = normalize(contact.spouse_display_name);
      const matches = allClients.filter(c => c.guid !== contact.guid && normalize(c.display_name) === spouseName);

      if (matches.length === 0) {
        result.unmatched.push(`${contact.display_name} (spouse: "${contact.spouse_display_name}")`);
        continue;
      }
      if (matches.length > 1) {
        result.ambiguous.push(`${contact.display_name} (spouse: "${contact.spouse_display_name}" matched ${matches.length} contacts)`);
        continue;
      }

      const spouse = matches[0];
      const pairKey = [contact.guid, spouse.guid].sort().join('|');
      if (linkedPairKeys.has(pairKey)) continue;

      try {
        const data: any = await this.apiService.post(`clients/family_members/${contact.guid}`, {
          guid: spouse.guid,
          relationship: 'Spouse',
          relationship_opposite: 'Spouse'
        });
        if (data?.success) {
          linkedPairKeys.add(pairKey);
          result.linkedCount++;
        }
      } catch (e) { /* skip this pair, keep going */ }
    }

    return result;
  }

}
